import React, { useState, useEffect, useCallback } from 'react';
import { Button, Alert, Form, Input, DatePicker, Typography, Space, Card, Row, Col } from 'antd';
import { SearchOutlined, CopyOutlined } from '@ant-design/icons';
import API from '../services/api'; // 导入统一的API实例
import * as echarts from 'echarts'; // 导入ECharts
import dayjs from 'dayjs';

const { Title } = Typography;

const TrafficQuery = () => {
  const [ip, setIp] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [trafficData, setTrafficData] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  const [loading, setLoading] = useState(false);

  // 初始化默认时间范围（当天）
  useEffect(() => {
    const today = dayjs(); // 使用当前时间
    
    setStartTime(today);
    setEndTime(today);
  }, []);

  // 验证日期范围
  const validateDateRange = (start, end) => {
    if (!start || !end) {
      return { isValid: false, message: '请选择开始时间和结束时间' };
    }

    if (start.isAfter(end)) {
      return { isValid: false, message: '开始时间不能晚于结束时间' };
    }

    // 计算时间差（天数）
    const daysDiff = end.diff(start, 'day') + 1;

    if (daysDiff > 3) {
      return { isValid: false, message: '查询时间跨度不能超过3天' };
    }

    return { isValid: true, message: '' };
  };

  // 截图并复制到剪贴板功能
  const copyChartToClipboard = (chartId, chartName) => {
    try {
      const chart = echarts.getInstanceByDom(document.getElementById(chartId));
      const dataURL = chart.getDataURL({
        pixelRatio: 2,
        backgroundColor: '#fff'
      });
      
      // 创建一个临时图片元素
      const img = new Image();
      img.src = dataURL;
      
      img.onload = function() {
        // 创建canvas元素
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        // 绘制图片到canvas
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        
        // 将canvas转换为blob，并复制到剪贴板
        canvas.toBlob(function(blob) {
          navigator.clipboard.write([
            new ClipboardItem({
              'image/png': blob
            })
          ]).then(() => {
            setCopyStatus(`${chartName}已复制到剪贴板`);
            setTimeout(() => setCopyStatus(''), 2000);
          }).catch(err => {
            console.error('复制到剪贴板失败:', err);
            setCopyStatus('复制失败，请重试');
            setTimeout(() => setCopyStatus(''), 2000);
          });
        });
      };
    } catch (error) {
      console.error('截图失败:', error);
      setCopyStatus('截图失败，请重试');
      setTimeout(() => setCopyStatus(''), 2000);
    }
  };

  // 查询单个IP流量
  const handleIpTrafficQuery = async () => {
    // 验证IP地址
    if (!ip.trim()) {
      setErrorMessage('请输入IP地址');
      setShowAlert(true);
      return;
    }

    // 验证日期范围
    const validation = validateDateRange(startTime, endTime);
    if (!validation.isValid) {
      setErrorMessage(validation.message);
      setShowAlert(true);
      return;
    }

    setLoading(true);
    try {
      const response = await API.get(`/api/admin/traffic/${ip}`, {
        params: {
          start_time: startTime ? startTime.format('YYYY-MM-DD') : '',
          end_time: endTime ? endTime.format('YYYY-MM-DD') : ''
        }
      });
      if (response.data.ipTraffic.length === 0) {
        setErrorMessage('暂无数据');
        setShowAlert(true);
      } else {
        setTrafficData(response.data.ipTraffic);
        setShowAlert(false);
      }
    } catch (error) {
      console.error('获取单个IP流量失败:', error);
      setErrorMessage('获取单个IP流量失败');
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const getUnit = (maxValue) => {
    if (maxValue >= 1e9) return { divisor: 1e9, unit: 'Gbps' };
    if (maxValue >= 1e6) return { divisor: 1e6, unit: 'Mbps' };
    if (maxValue >= 1e3) return { divisor: 1e3, unit: 'Kbps' };
    return { divisor: 1, unit: 'bps' };
  };

  const drawTrafficChart = useCallback((chart, dataArray) => {
    const labels = dataArray.map(data => {
      const formattedHour = data.hour < 10 ? `0${data.hour}` : data.hour;
      const formattedMinute = data.minute === 0 ? '00' : data.minute;
      return `${formattedHour}:${formattedMinute}`;
    });

    const inSizeRaw = dataArray.map(data => (data.in_size * 8));  
    const outSizeRaw = dataArray.map(data => (data.out_size * 8));  

    const maxValue = Math.max(...inSizeRaw, ...outSizeRaw);
    const { divisor, unit } = getUnit(maxValue);

    const inSize = inSizeRaw.map(size => (size / divisor).toFixed(2));
    const outSize = outSizeRaw.map(size => (size / divisor).toFixed(2));

    const trafficOption = {
      title: {
        text: '流量统计图表',
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 500,
          color: '#333'
        },
        padding: [10, 0, 0, 0]
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: '#ddd',
        textStyle: {
          color: '#333'
        },
        formatter: (params) => {
          const { time, hour, minute } = dataArray[params[0].dataIndex];
          const formattedTime = `${time} ${hour < 10 ? `0${hour}` : hour}:${minute === 0 ? '00' : minute}`;
          let content = `时间: ${formattedTime}<br/>`;
          params.forEach(item => {
            content += `${item.seriesName}: ${item.value} ${unit}<br/>`;
          });
          return content;
        }
      },
      legend: {
        data: [`输入流量 (${unit})`, `输出流量 (${unit})`],
        bottom: '0%',
        textStyle: {
          fontSize: 12,
          color: '#333'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        top: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: labels,
        axisLine: {
          lineStyle: {
            color: '#ddd'
          }
        },
        axisLabel: {
          color: '#333',
          fontSize: 12
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: '#ddd'
          }
        },
        axisLabel: {
          formatter: `{value} ${unit}`,
          color: '#333',
          fontSize: 12
        },
        splitLine: {
          lineStyle: {
            color: '#eee'
          }
        }
      },
      series: [
        {
          name: `输入流量 (${unit})`,
          type: 'line',
          data: inSize,
          smooth: true,
          lineStyle: {
            width: 2,
            color: '#1890ff'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [{
                offset: 0,
                color: 'rgba(24, 144, 255, 0.2)'
              }, {
                offset: 1,
                color: 'rgba(24, 144, 255, 0.05)'
              }]
            }
          }
        },
        {
          name: `输出流量 (${unit})`,
          type: 'line',
          data: outSize,
          smooth: true,
          lineStyle: {
            width: 2,
            color: '#52c41a'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [{
                offset: 0,
                color: 'rgba(82, 196, 26, 0.2)'
              }, {
                offset: 1,
                color: 'rgba(82, 196, 26, 0.05)'
              }]
            }
          }
        }
      ]
    };
    chart.setOption(trafficOption);
  }, []);

  const drawConnectionChart = useCallback((chart, dataArray) => {
    const labels = dataArray.map(data => {
      const formattedHour = data.hour < 10 ? `0${data.hour}` : data.hour;
      const formattedMinute = data.minute === 0 ? '00' : data.minute;
      return `${formattedHour}:${formattedMinute}`;
    });

    const maxInCount = dataArray.map(data => data.average_connect_in_count); // 最大输入新建连接
    const maxOutCount = dataArray.map(data => data.average_connect_out_count); // 最大输出新建连接
  
    const connectionOption = {
      title: {
        text: '连接统计图表',
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 500,
          color: '#333'
        },
        padding: [10, 0, 0, 0]
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: '#ddd',
        textStyle: {
          color: '#333'
        },
        formatter: (params) => {
          const { time, hour, minute } = dataArray[params[0].dataIndex];
          const formattedTime = `${time} ${hour < 10 ? `0${hour}` : hour}:${minute === 0 ? '00' : minute}`;
          let content = `时间: ${formattedTime}<br/>`;
          params.forEach(item => {
            content += `${item.seriesName}: ${item.value} 个<br/>`;
          });
          return content;
        }
      },
      legend: {
        data: ['最大输入新建连接', '最大输出新建连接'],
        bottom: '0%',
        textStyle: {
          fontSize: 12,
          color: '#333'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        top: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: labels,
        axisLine: {
          lineStyle: {
            color: '#ddd'
          }
        },
        axisLabel: {
          color: '#333',
          fontSize: 12
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: '#ddd'
          }
        },
        axisLabel: {
          color: '#333',
          fontSize: 12
        },
        splitLine: {
          lineStyle: {
            color: '#eee'
          }
        }
      },
      series: [
        {
          name: '最大输入新建连接',
          type: 'line',
          data: maxInCount,
          smooth: true,
          lineStyle: {
            width: 2,
            color: '#1890ff'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [{
                offset: 0,
                color: 'rgba(24, 144, 255, 0.2)'
              }, {
                offset: 1,
                color: 'rgba(24, 144, 255, 0.05)'
              }]
            }
          }
        },
        {
          name: '最大输出新建连接',
          type: 'line',
          data: maxOutCount,
          smooth: true,
          lineStyle: {
            width: 2,
            color: '#52c41a'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [{
                offset: 0,
                color: 'rgba(82, 196, 26, 0.2)'
              }, {
                offset: 1,
                color: 'rgba(82, 196, 26, 0.05)'
              }]
            }
          }
        }
      ]
    };
    chart.setOption(connectionOption);
  }, []);

  useEffect(() => {
    if (trafficData.length > 0) {
      const trafficChart = echarts.init(document.getElementById('trafficChart'));
      const connectionChart = echarts.init(document.getElementById('connectionChart'));

      drawTrafficChart(trafficChart, trafficData);
      drawConnectionChart(connectionChart, trafficData);

      // 添加resize监听，确保图表大小自适应容器
      window.addEventListener('resize', () => {
        trafficChart.resize();
        connectionChart.resize();
      });

      // 销毁图表实例以防止内存泄漏
      return () => {
        trafficChart.dispose();
        connectionChart.dispose();
        window.removeEventListener('resize', () => {
          trafficChart.resize();
          connectionChart.resize();
        });
      };
    }
  }, [trafficData, drawTrafficChart, drawConnectionChart]);

  return (
    <>
      <div style={{
        maxWidth: '1200px',
        padding: '30px 24px',
        margin: '0 auto'
      }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* 标题和说明 */}
          <div style={{ textAlign: 'center' }}>
            <Title level={3} style={{ marginBottom: 16 }}>
              管理员流量查询
            </Title>
            
            <Alert
              message="支持自定义日期查询，时间跨度最多3天"
              type="info"
              showIcon
              style={{ maxWidth: 600, margin: '0 auto' }}
            />
          </div>

          {/* 查询表单 */}
          <Row justify="center">
            <Col xs={24} md={20} lg={16}>
              {showAlert && (
                <Alert
                  message={errorMessage}
                  type="error"
                  closable
                  onClose={() => setShowAlert(false)}
                  style={{ marginBottom: 24 }}
                />
              )}

              <Card>
                <Form
                  layout="vertical"
                >
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item label="开始时间">
                        <DatePicker
                          value={startTime}
                          onChange={setStartTime}
                          style={{ width: '100%' }}
                          placeholder="选择开始时间"
                          disabled={loading}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item label="结束时间">
                        <DatePicker
                          value={endTime}
                          onChange={setEndTime}
                          style={{ width: '100%' }}
                          placeholder="选择结束时间"
                          disabled={loading}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col flex="auto">
                      <Form.Item label="IP地址">
                        <Input
                          placeholder="输入IP地址"
                          value={ip}
                          onChange={e => setIp(e.target.value)}
                          onPressEnter={handleIpTrafficQuery}
                          disabled={loading}
                        />
                      </Form.Item>
                    </Col>
                    <Col flex="none" style={{ display: 'flex', alignItems: 'end' }}>
                      <Form.Item>
                        <Button
                          type="primary"
                          onClick={handleIpTrafficQuery}
                          icon={<SearchOutlined />}
                          loading={loading}
                          disabled={loading}
                        >
                          查询
                        </Button>
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </Card>
            </Col>
          </Row>

        {/* 图表展示 */}
        {trafficData.length > 0 && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card title="流量统计图表" 
              extra={
                <Button 
                  type="default"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyChartToClipboard('trafficChart', '流量统计图表')}
                >
                  复制图表
                </Button>
              }
            >
              <div id="trafficChart" style={{ 
                height: '350px', 
                width: '100%'
              }}></div>
            </Card>
            
            <Card title="连接统计图表"
              extra={
                <Button 
                  type="default"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyChartToClipboard('connectionChart', '连接统计图表')}
                >
                  复制图表
                </Button>
              }
            >
              <div id="connectionChart" style={{ 
                height: '350px', 
                width: '100%'
              }}></div>
            </Card>
          </Space>
        )}
        </Space>
      </div>
      
      {/* 复制提示 - 完全脱离文档流，不影响页面布局 */}
      {copyStatus && (
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '24px',
          zIndex: 1050,
          maxWidth: '300px',
          pointerEvents: 'none' // 防止影响其他元素
        }}>
          <Alert 
            message={copyStatus}
            type="success"
            showIcon
            closable
            onClose={() => setCopyStatus('')}
            style={{
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              borderRadius: '6px',
              pointerEvents: 'auto' // 恢复Alert本身的交互
            }}
          />
        </div>
      )}
    </>
  );
};

export default TrafficQuery;
